import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { object, string } from "yup";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";

import axios from "../../axios";
import HeaderInner from "../../Components/HeaderInner";
import Title from "../../new-components/title/Title";
import NewFooter from "../../NewFooter";
import PhoneInputComponent from "../../Components/PhoneInputComponent";
import CustomBusinessServiceLoader from "../../Components/Loaders/CustomBusinessServiceLoader";
import LocationVerificationModal from "../../Components/LocationVerificationModal";

import { useBusinessNameContext } from "../../context/BusinessName";
import { trackMenuItemViewed, trackOrderSubmitted } from "../../utils/mixpanel";
import PaymentOptionList from "../../Components/PaymentOptionList";
import { normalizePaymentMethods } from "../../utils/paymentMethods";

const ADDITIONAL_HOURS_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4];

function LateCheckoutdetails() {
  const { room_uuid, late_checkout_uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const loaderRef = useRef(null);

  const { businessName } = useBusinessNameContext();

  const new_design = localStorage.getItem("new_design");
  const theme = new_design === "true";

  const lang = sessionStorage.getItem("lang");

  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [show, setShow] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const [paymentUrl, setPaymentUrl] = useState("");

  const [headerTitle, setHeaderTitle] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [lateCheckoutUuid, setLateCheckoutUuid] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedOnlinePaymentMethod, setSelectedOnlinePaymentMethod] =
    useState("credit card");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    additional_hours: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: "",
    additional_hours: "",
  });
  const [phoneWithoutCode, setPhoneWithoutCode] = useState("");
  const userSchema = object({
    name: string().required(t("The name field is required.")),

    phone: string()
      .required(t("The phone field is required."))
      .test(
        "phone-number",
        t("The phone field is required."),
        () => phoneWithoutCode.trim() !== ""
      ),

    email: string()
      .email(t("The email must be a valid email address."))
      .required(t("The email field is required.")),
      
    additional_hours: string().required(
      t("The additional hours field is required.")
    ),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleShow = () => {
    setShow(true);

    ReactGA.event({
      category: "Open popup Late Checkout detail",
      action: "Click",
      value: "Late Checkout",
    });
  };

  const handleClose = () => {
    setShow(false);

    ReactGA.event({
      category: "Close popup Late Checkout detail",
      action: "Click",
      value: "Late Checkout",
    });
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `/${room_uuid}/late-checkout/${late_checkout_uuid}`,
          {
            headers: {
              lang,
            },
          }
        );

        if (res.data.status === false || res.data.success === false) {
          navigate(`/${room_uuid}/qr-scan`);
          return;
        }

        const data = res.data.data;
        const lateCheckout = data?.lateCheckout;

        setHeaderTitle(
          data?.center_header || data?.title || t("Late Checkout")
        );

        setTitle(data?.title || t("Late Checkout"));

        setDescription(lateCheckout?.description || "");

        setPrice(lateCheckout?.price || "");

        setLateCheckoutUuid(lateCheckout?.uuid || late_checkout_uuid || "");

        const methods = normalizePaymentMethods(lateCheckout?.payment_methods);

        setPaymentMethods(methods);
        setSelectedPaymentMethod(methods[0]?.payment_method || "");

        trackMenuItemViewed({
          item_id: lateCheckout?.uuid || late_checkout_uuid || "",
          item_name: data?.title || "Late Checkout",
          category_name: "Late Checkout",
        });
      } catch (err) {
        console.error("Late checkout get error:", err);

        if (err?.response?.status === 404) {
          navigate(`/${room_uuid}/qr-scan`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (room_uuid && late_checkout_uuid) {
      getData();
    }
  }, [room_uuid, late_checkout_uuid]);

  const totalPrice = useMemo(() => {
    const pricePerHour = Number(price);
    const additionalHours = Number(form.additional_hours);

    if (!Number.isFinite(pricePerHour) || !Number.isFinite(additionalHours)) {
      return 0;
    }

    return pricePerHour * additionalHours;
  }, [price, form.additional_hours]);

  const handleBook = async () => {
    try {
      await userSchema.validate(form, {
        abortEarly: false,
      });

      setErrors({
        name: "",
        phone: "",
        email: "",
        additional_hours: "",
      });

      setShowLocationModal(true);
    } catch (err) {
      const validationErrors = {
        name: "",
        phone: "",
        email: "",
        additional_hours: "",
      };

      if (err?.inner) {
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
      }

      setErrors(validationErrors);
    }
  };

  const submitLateCheckoutOrder = async () => {
    setIsDisabled(true);

    try {
      const answer = await userSchema.validate(form);

      const formData = new FormData();

      formData.append("name", answer.name);
      formData.append("phone", answer.phone);
      formData.append("email", answer.email);
      formData.append("additional_hours", answer.additional_hours);
      formData.append("payment_method", selectedPaymentMethod);

      const res = await axios.post(
        `/${room_uuid}/late-checkout/${lateCheckoutUuid}/order`,
        formData,
        {
          headers: {
            lang,
          },
        }
      );

      ReactGA.event({
        category: "Book service Late Checkout",
        action: "Click",
        value: "Late Checkout",
      });

      if (res.data.success) {
        const amount = totalPrice;

        trackOrderSubmitted({
          order_id: res.data.data?.orderId || "",
          order_number: res.data.data?.orderNumber || "",
          service_type: "late_checkout",
          order_source: localStorage.getItem("room_type") || "room",
          items_count: 1,

          subtotal: amount,
          tax: 0,
          delivery_fee: 0,
          other_fees: 0,
          total_amount: amount,

          is_external_service: false,
        });

        if (res.data.data?.payment_url) {
          if (res.data.data.is_embedded) {
            setPaymentUrl(res.data.data.payment_url);
          } else {
            window.location.href = res.data.data.payment_url;
          }

          return;
        }

        navigate(`/${room_uuid}/LateCheckoutOrderPlaced`, {
          state: {
            metadata: {
              res: res.data.data,
            },
          },
        });
      }
    } catch (err) {
      console.error("Late checkout order error:", err);
    } finally {
      setIsDisabled(false);
    }
  };

  const handleLocationVerified = () => {
    setShowLocationModal(false);
    submitLateCheckoutOrder();
  };

  const handleLoad = () => {
    if (loaderRef.current) {
      loaderRef.current.style.display = "none";
      loaderRef.current.style.visibility = "hidden";
      loaderRef.current.remove();
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, []);

  return (
    <div className={theme ? "food" : ""}>
      {theme ? (
        <Title title={headerTitle} />
      ) : (
        <HeaderInner headerName={headerTitle} />
      )}

      <main
        className="home-main pad-top-0 back-fe"
        style={{ paddingBottom: "1rem" }}
      >
        {loading ? (
          <CustomBusinessServiceLoader
            businessName={businessName}
            loader="spa"
          />
        ) : (
          <>
            <div className="padd-div">
              <div className="img-pnl">
                <img
                  className=""
                  src={"/late-checkout.png"}
                  style={{ marginBottom: "1rem" }}
                />
              </div>
              {description && (
                <>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: description.replaceAll("\n", "</br>"),
                    }}
                  />

                  <br />
                </>
              )}

              {price && (
                <>
                  <div
                    style={{ fontWeight: "600", textTransform: "capitalize" }}
                  >
                    {t("Price per Hour")}:
                  </div>

                  <div className="d-flex">
                    <div>{price}</div>
                  </div>

                  <br />
                </>
              )}
            </div>
          </>
        )}
        <div className="body-container">
          <div className="content-pnl w-100">
            <div style={{ padding: "0 2rem" }}>
              <Row className="justify-content-center">
                {/* Additional hours */}
                <Col xs="12">
                  <Form.Group className="mb-3 dark-placeholder i-f-small">
                    <Form.Select
                      name="additional_hours"
                      value={form.additional_hours}
                      onChange={handleInputChange}
                      className="w-100 additional_hours_select"
                    >
                      <option value="">{t("Select additional hours")}</option>

                      {ADDITIONAL_HOURS_OPTIONS.map((hours) => (
                        <option key={hours} value={hours}>
                          {hours}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {errors.additional_hours && (
                    <span className="err_msg">{errors.additional_hours}</span>
                  )}
                </Col>
                {/* Name */}
                <Col xs="12">
                  <Form.Group className="mb-3 dark-placeholder i-f-small">
                    <Form.Control
                      type="text"
                      placeholder={t("Your Name")}
                      name="name"
                      value={form.name}
                      onChange={handleInputChange}
                    />
                  </Form.Group>

                  {errors.name && (
                    <span className="err_msg">{errors.name}</span>
                  )}
                </Col>

                {/* Phone */}
                <Col xs="12">
                  <Form.Group className="mb-3 dark-placeholder i-f-small w-100">
                    <PhoneInputComponent
                      value={form.phone}
                      onChange={(phone, country) => {
                        setForm((prev) => ({
                          ...prev,
                          phone,
                        }));

                        const numberWithoutCode = phone.slice(
                          country?.dialCode?.length || 0
                        );

                        setPhoneWithoutCode(numberWithoutCode);

                        setErrors((prev) => ({
                          ...prev,
                          phone: "",
                        }));
                      }}
                    />
                  </Form.Group>

                  {errors.phone && (
                    <span className="err_msg">{errors.phone}</span>
                  )}
                </Col>

                {/* Email */}
                <Col xs="12">
                  <Form.Group className="mb-3 dark-placeholder i-f-small">
                    <Form.Control
                      type="email"
                      placeholder={t("Your Email")}
                      name="email"
                      value={form.email}
                      onChange={handleInputChange}
                    />
                  </Form.Group>

                  {errors.email && (
                    <span className="err_msg">{errors.email}</span>
                  )}
                </Col>
              </Row>
            </div>
            <div
              className="d-flex align-items-center justify-content-between mt-3 mb-4 p-3"
              style={{
                fontWeight: "600",
                fontSize: "16px",
                backgroundColor: "#f0f0f0",
              }}
            >
              <span>{t("Total Price")}:</span>

              <span>{totalPrice.toFixed(2)}</span>
            </div>
            <div className="spacer-40" />

            {paymentMethods.length > 0 && !paymentUrl && (
              <div className="mb-4">
                <div className="full-heading payment flex">
                  <h2>{t("Payment Options")}</h2>
                </div>

                <PaymentOptionList
                  selected={selectedPaymentMethod}
                  setSelected={setSelectedPaymentMethod}
                  methods={paymentMethods}
                  setSelectedOnlinePaymentMethod={
                    setSelectedOnlinePaymentMethod
                  }
                />
              </div>
            )}

            {!paymentUrl && (
              <div className="padd-div text-center mb-2">
                <Button
                  className="reg-btn large more-pad font-dark"
                  onClick={handleBook}
                  disabled={isDisabled || !selectedPaymentMethod}
                >
                  {t("request")}
                </Button>
              </div>
            )}
            <p
              className="text-center"
              style={{
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              {t(
                "Requests are subject to availability. Rejected prepaid requests will be fully refunded."
              )}
            </p>
          </div>
        </div>
      </main>

      {theme && <NewFooter />}

      <LocationVerificationModal
        show={showLocationModal}
        room_uuid={room_uuid}
        theme={theme}
        onVerified={handleLocationVerified}
      />
    </div>
  );
}

export default LateCheckoutdetails;
